import { formatPrice, formatQuantity } from "@common/utils/string";
import {
  ADDON_PRODUCT_STORAGE_TYPES,
  ADDON_PRODUCT_TRANSFORMS_TYPES,
} from "@stores/addons/constants";
import { AddOnProductTransformsType, IAddOnTier } from "@stores/addons/types";
import { offerMeteredTransforms } from "@stores/config/utils";
import { ICreatePurchaseAddOn, IMeterKey } from "@stores/purchases/types";
import { getPayAsYouGoMeterValues } from "@stores/subscriptions/utils";
import {
  IAddOnContext,
  IMeteredAddOnUsage,
  IPurchaseContext,
  IAddOnRowItem,
  IUpsertContext,
} from "./types";

export const hasStorage = ({ subscribedByType }: IUpsertContext): boolean =>
  ADDON_PRODUCT_STORAGE_TYPES.some((type) => !!subscribedByType[type]);

/** a plan change re-sends the legacy prepaid storage so its quantity survives */
export const carryOverStorage = (
  ctx: IUpsertContext,
): ICreatePurchaseAddOn[] => {
  const units = ctx.subscribedByType.dwh?.prepaid_units;

  return units === undefined ? [] : ctx.getUpsertAddOnsFor("dwh", units);
};

export const hasTransforms = ({
  subscribedByType,
}: IPurchaseContext): boolean =>
  ADDON_PRODUCT_TRANSFORMS_TYPES.some((type) => !!subscribedByType[type]);

export const hasProductAnalytics = ({
  subscribedByType,
}: IPurchaseContext): boolean =>
  !!subscribedByType["product-analytics"] ||
  !!subscribedByType["product-analytics-free"];

export const contractedPricePerUnit = ({
  subscribed,
  addOn,
}: IAddOnContext): number =>
  subscribed?.price_per_unit ?? addOn?.default_price_per_unit ?? 0;

const perTerm = ({ isYearly }: IAddOnContext, price: number): string =>
  `${formatPrice(price)}/${isYearly ? "year" : "month"}`;

export const prepaidEstimate = (
  ctx: IAddOnContext,
  units: number,
): string | undefined =>
  ctx.addOn && perTerm(ctx, ctx.addOn.default_price_per_unit * units);

export const baseFeeEstimate = (ctx: IAddOnContext): string | undefined =>
  ctx.addOn && perTerm(ctx, ctx.addOn.default_base_fee);

export const perRunEstimate = (ctx: IAddOnContext): string | undefined =>
  ctx.addOn && `${formatPrice(contractedPricePerUnit(ctx))}/run`;

export const subscribedUnits = ({ subscribed }: IAddOnContext): number =>
  subscribed?.prepaid_units ?? 0;

/** the fee an unsubscribed card must not quote: the catalogue row resolves either way */
export const subscribedBaseFee = ({
  subscribed,
  addOn,
}: IAddOnContext): number | null =>
  subscribed ? (addOn?.default_base_fee ?? null) : null;

/** the tier whose published quantity the subscription actually bought */
export const subscribedTier = ({
  subscribed,
  addOn,
}: IAddOnContext): IAddOnTier | undefined =>
  addOn?.product_tiers?.find(
    ({ quantity }) => quantity === subscribed?.prepaid_units,
  );

/**
 * What the prepaid rows cost for the cycle. Shared so the manage card and the
 * invoice row cannot quote different totals for the same subscription.
 */
export const prepaidTotal = ({
  subscribed,
  addOn,
}: IAddOnContext): number | null =>
  subscribed && addOn
    ? addOn.default_price_per_unit * subscribed.prepaid_units
    : null;

/**
 * The cycle's metered charge: units reported against the meter, billed at the
 * contracted rate. Only a metered `getUsage` returns `billableUnits`, so its
 * presence is also the check that this row applies.
 */
export const meteredLineItem = (
  ctx: IAddOnContext,
  unit: string,
  precision?: number,
): IAddOnRowItem | undefined => {
  const usage = ctx.getUsage();

  if (!usage || !("billableUnits" in usage)) {
    return undefined;
  }

  return {
    detail: `${formatQuantity(usage.usage, precision)} ${unit}`,
    price: formatPrice(usage.billableUnits * (ctx.getPricePerUnit() ?? 0)),
  };
};

/** pay-per-run transforms: the cycle's billable runs at the contracted rate */
export const transformsRunsLineItem = (
  ctx: IAddOnContext,
): IAddOnRowItem | undefined => {
  const usage = ctx.getUsage();
  const billableRuns =
    usage && "billableUnits" in usage ? usage.billableUnits : 0;
  const cost = billableRuns * (ctx.getPricePerUnit() ?? 0);

  return cost
    ? { detail: billableRuns.toLocaleString(), price: formatPrice(cost) }
    : undefined;
};

/** legacy flat-fee transforms: the tier and its monthly fee */
export const transformsFeeLineItem = (
  ctx: IAddOnContext,
  tier: string,
): IAddOnRowItem | undefined => {
  const fee = subscribedBaseFee(ctx);

  return fee === null ? undefined : { detail: tier, price: formatPrice(fee) };
};

/** the same, where the charge is a tier's flat price rather than per-unit */
export const tieredTotal = (ctx: IAddOnContext): number | null => {
  const tier = subscribedTier(ctx);

  return ctx.addOn && tier
    ? ctx.addOn.default_price_per_unit * tier.quantity
    : null;
};

/** the lifetime free pool of a pay-as-you-go add-on, published before subscribing */
export const contractedFreeUnits = ({
  subscribed,
  addOn,
}: IAddOnContext): number => subscribed?.free_units ?? addOn?.free_units ?? 0;

/** the same transforms type is sold on both deployments with different copy */
export const transformsTitle = (isCloudHosted: boolean): string =>
  isCloudHosted ? "Transforms" : "Advanced transforms";

/**
 * The one transforms tier on offer to a purchase that has none, so the four
 * descriptors cannot disagree about which of them shows an unsubscribed card.
 */
export const offeredTransformsType = (
  ctx: IPurchaseContext,
): AddOnProductTransformsType | undefined => {
  if (hasTransforms(ctx)) {
    return undefined;
  }
  if (offerMeteredTransforms(ctx.config)) {
    // cloud has no metered row to sell yet
    return ctx.isCloudHosted ? undefined : "transforms-advanced-metered";
  }
  return ctx.isCloudHosted ? "transforms-basic" : "transforms-advanced";
};

/** the tiers this purchase's term and deployment can buy under the flag, basic first */
export const offeredTransformsTypes = ({
  addOnsByType,
  config,
}: Pick<
  IPurchaseContext,
  "addOnsByType" | "config"
>): AddOnProductTransformsType[] =>
  ADDON_PRODUCT_TRANSFORMS_TYPES.filter(
    (type) =>
      addOnsByType[type] &&
      !!addOnsByType[type].is_metered === offerMeteredTransforms(config),
  );

/**
 * The type a chosen transforms option buys. The options form names the tier
 * by simplified alias ("basic") while the subscription names it by product
 * type, and the metered flag decides which variant of that tier is on sale.
 * No tier at all means the catalogue offered none, so it answers with the
 * first one that would have been on sale rather than throwing.
 */
export const transformsTypeFor = (
  { config }: Pick<IPurchaseContext, "config">,
  tier: string | undefined,
): AddOnProductTransformsType => {
  const basic = tier === undefined || tier.includes("basic");

  if (offerMeteredTransforms(config)) {
    return basic ? "transforms-basic-metered" : "transforms-advanced-metered";
  }

  return basic ? "transforms-basic" : "transforms-advanced";
};

/**
 * Gated on the meter rather than on `ctx.subscribed`: harbormaster builds
 * `meters` by re-indexing the subscription's live metered rows under their
 * `meter_name`, so the key is present exactly while the add-on is, and absent
 * as soon as the row is cancelled. Reading the guard off the data this
 * actually consumes keeps it verifiable here instead of resting on that
 * cross-service invariant.
 */
export const transformsUsage = (
  ctx: IAddOnContext,
  meterKey: IMeterKey,
): IMeteredAddOnUsage | undefined => {
  const meter = ctx.purchase.meters?.[meterKey];

  if (!meter) {
    return undefined;
  }

  const includedUnits = contractedFreeUnits(ctx);
  const hasQuota = typeof meter.meter_free_units === "number";
  const usage = meter.meter_value ?? 0;
  const payAsYouGo = getPayAsYouGoMeterValues({
    meterValue: meter.meter_value,
    freeUnits: includedUnits,
    meterFreeUnits: meter.meter_free_units,
  });

  return {
    hasQuota,
    usage,
    includedUnits,
    ...payAsYouGo,
    // `getPayAsYouGoMeterValues` splits the pool as if it were untouched when
    // harbormaster has not materialised it yet, which is right for the graph
    // but not for the bill: there is nothing to draw down, so every recorded
    // run bills. Resolved here so the row and the usage panel cannot disagree.
    billableUnits: hasQuota ? payAsYouGo.billableUnits : usage,
  };
};
